import { Buffer } from 'buffer';
import type { UploadBase64Props, UploadFileProps } from './types';
import { uploadWithAwsS3Multipart } from './uppyUploader';
import { getStandardizedFileName } from './filenameUtils';
import { logUploadError, logUploadEvent } from './loggingUtils';

const uploadViaUppy = async (props: UploadFileProps) => {
  const { correlationId, file, updateProgress, r2UploadsApi } = props;

  logUploadEvent('Studio: Upload Started', file, {
    correlationId,
    storageProvider: 'r2',
  });

  try {
    const { assetUrl, fileName } = await uploadWithAwsS3Multipart({
      correlationId,
      file,
      onProgress: updateProgress,
      r2UploadsApi,
    });

    return {
      assetUrl,
      fileName,
      correlationId,
      file,
    };
  } catch (error) {
    // Extract upload context from uppyUploader errors
    const uploadContext = (error as any)?.uploadContext;

    logUploadError('Studio: Upload Failed', file, error, {
      correlationId,
      storageProvider: 'r2',
      ...(uploadContext && {
        bytesUploaded: uploadContext.bytesUploaded,
        presignedUrl: uploadContext.presignedUrl,
        hadUploadProgress: uploadContext.hadUploadProgress,
        uploadStep: uploadContext.uploadStep,
        originalErrorName: uploadContext.originalErrorName,
        originalErrorMessage: uploadContext.originalErrorMessage,
        errorDetails: uploadContext.errorDetails,
        causeName: uploadContext.causeName,
        causeMessage: uploadContext.causeMessage,
        requestUrl: uploadContext.requestUrl,
        statusCode: uploadContext.statusCode,
        statusOrigin: uploadContext.statusOrigin,
        statusText: uploadContext.statusText,
        transportOutcome: uploadContext.transportOutcome,
      }),
    });
    throw error;
  }
};

export const uploadFile = async (props: UploadFileProps) =>
  uploadViaUppy(props);

export const uploadBase64 = async (props: UploadBase64Props) => {
  const buffer = Buffer.from(props.base64Str, 'base64');
  const fileName = props.fileName || 'original';
  const standardizedFileName = getStandardizedFileName(fileName);
  const file = new File([buffer], standardizedFileName, {
    type: props.contentType,
  });

  return uploadViaUppy({
    correlationId: props.correlationId,
    file,
    updateProgress: props.updateProgress,
    r2UploadsApi: props.r2UploadsApi,
  });
};

export * from './fileToBase64';
